// Status pill matching the design's StatusPill: dot + label, status-tinted
// background for active states, border-only for closed, archive tint for
// archived. Maps `RequestStatus` enum values to locale keys and color tones.

import { type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { RequestStatus } from '@magimp/shared';

type Tone = 'neutral' | 'info' | 'warn' | 'error' | 'success' | 'closed' | 'archive';

// WHY: PROCESSED shares the `info` tone with IN_WORK; the user-facing
//   copy (`status.clientDecision` vs `status.inWork`) is the
//   differentiator. RETURN drops the red `error` tone for neutral grey
//   to match its draft-like role in the workflow — the standalone
//   return-reason banner in RequestDrawer carries the "action needed"
//   signal via warn tokens, so severity is no longer doubled on the
//   pill.
const TONE_BY_STATUS: Record<RequestStatus, Tone> = {
  [RequestStatus.DRAFT]: 'neutral',
  // WHY (#962): READY_FOR_WORK is the "manager handed off, logist has
  //   not picked it up yet" pre-work state. The `warn` (ochre) tone
  //   signals attention is needed without raising it to error severity,
  //   matching the same ochre tokens the SLA hot-state already uses.
  [RequestStatus.READY_FOR_WORK]: 'warn',
  [RequestStatus.IN_WORK]: 'info',
  [RequestStatus.RETURN]: 'neutral',
  [RequestStatus.PROCESSED]: 'info',
  // WHY (#1835): POSTPONED is an active, non-terminal "parked" state. The
  //   `warn` (ochre) tone signals attention is needed without raising it to
  //   error severity, matching READY_FOR_WORK above. Foundation only — no
  //   transition produces POSTPONED yet (#1836/#1837).
  [RequestStatus.POSTPONED]: 'warn',
  [RequestStatus.CLOSED]: 'closed',
  [RequestStatus.ARCHIVE]: 'archive',
  [RequestStatus.SUCCESS]: 'success',
  [RequestStatus.REFUSED]: 'error',
  // WHY (#2133): CANNOT_TRANSPORT is a terminal negative outcome — shares the
  //   REFUSED `error` tone. Compile shim only; the filter chip / any visual
  //   refinement lands in #2139.
  [RequestStatus.CANNOT_TRANSPORT]: 'error',
};

// INVARIANT: PROCESSED has two distinct label surfaces.
//   * `STATUS_LABEL_KEYS` — end-user UI (pill, filter chips, list rows,
//     mobile cards, finalisation flows). Resolves PROCESSED through the
//     `status.clientDecision` i18n key because the user-facing meaning
//     is the manager's decision step, not the technical state.
//   * `STATUS_LABEL_KEYS_TECHNICAL` — audit ledger / developer surfaces,
//     where precision matters. Resolves PROCESSED through the canonical
//     `status.processed` key so transitions like IN_WORK → PROCESSED
//     read technically (not as the end-user decision-step prompt).
//   Backend `RequestStatus.PROCESSED` is the single underlying value;
//   only the displayed copy diverges.
export const STATUS_LABEL_KEYS = {
  [RequestStatus.DRAFT]: 'status.draft',
  // WHY (#960 foundation): single i18n key for READY_FOR_WORK across
  //   end-user + technical surfaces. #962 may diverge if the final UX
  //   needs distinct copy. The locale catalog ships placeholder strings
  //   so the pill renders in the brief window before #962 lands.
  [RequestStatus.READY_FOR_WORK]: 'status.readyForWork',
  [RequestStatus.IN_WORK]: 'status.inWork',
  [RequestStatus.RETURN]: 'status.return',
  [RequestStatus.PROCESSED]: 'status.clientDecision',
  [RequestStatus.POSTPONED]: 'status.postponed',
  [RequestStatus.CLOSED]: 'status.closed',
  [RequestStatus.ARCHIVE]: 'status.archive',
  [RequestStatus.SUCCESS]: 'status.success',
  [RequestStatus.REFUSED]: 'status.refused',
  // WHY (#2133): dedicated label key for the terminal «cannot transport»
  //   outcome. Compile shim — renders if a CANNOT_TRANSPORT row reaches the
  //   pill; the filter chip lands in #2139.
  [RequestStatus.CANNOT_TRANSPORT]: 'status.cannotTransport',
} as const satisfies Record<RequestStatus, string>;

export const STATUS_LABEL_KEYS_TECHNICAL = {
  [RequestStatus.DRAFT]: 'status.draft',
  [RequestStatus.READY_FOR_WORK]: 'status.readyForWork',
  [RequestStatus.IN_WORK]: 'status.inWork',
  [RequestStatus.RETURN]: 'status.return',
  [RequestStatus.PROCESSED]: 'status.processed',
  [RequestStatus.POSTPONED]: 'status.postponed',
  [RequestStatus.CLOSED]: 'status.closed',
  [RequestStatus.ARCHIVE]: 'status.archive',
  [RequestStatus.SUCCESS]: 'status.success',
  [RequestStatus.REFUSED]: 'status.refused',
  [RequestStatus.CANNOT_TRANSPORT]: 'status.cannotTransport',
} as const satisfies Record<RequestStatus, string>;

const STYLES: Record<Tone, CSSProperties> = {
  neutral: { background: 'var(--status-neutral-bg)', color: 'var(--status-neutral-fg)' },
  info: { background: 'var(--status-info-bg)', color: 'var(--status-info-fg)' },
  warn: { background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' },
  error: { background: 'var(--status-error-bg)', color: 'var(--status-error-fg)' },
  success: { background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' },
  closed: { background: 'transparent', color: 'var(--fg-1)', border: '1px solid var(--border-1)' },
  archive: { background: 'var(--status-archive-bg)', color: 'var(--status-archive-fg)' },
};

export interface RequestStatusPillProps {
  status: RequestStatus;
  size?: 'sm' | 'md';
}

export function RequestStatusPill({ status, size = 'md' }: RequestStatusPillProps) {
  const { t } = useTranslation('requests');
  const tone = TONE_BY_STATUS[status];
  const h = size === 'sm' ? 20 : 22;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: h,
        padding: `0 ${size === 'sm' ? 7 : 9}px`,
        borderRadius: 'var(--r-1)',
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        ...STYLES[tone],
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
      {t(STATUS_LABEL_KEYS[status])}
    </span>
  );
}
